import { memo, useEffect, useMemo, useRef } from "react";
import { Toaster } from "react-hot-toast";
import { IntlProvider } from "react-intl";
import type { LinksFunction, ShouldRevalidateFunction } from "react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
  useRouteLoaderData,
  useSearchParams,
} from "react-router";

import { getContext } from "./.server/context.server";
import { getSessionServerContext } from "./.server/sessions.server";
import { fetchTranslationsFromKv } from "./lib/utils-server/locale.server";

import { ContentfulLivePreviewProvider } from "@contentful/live-preview/react";

// import { useNonce } from "@shopify/hydrogen";
import { SiteProvider } from "./contexts";
import { GA4 } from "./google-analytics";
import { DEFAULT_COUNTRY_CODES } from "./lib/constants";
import { getCountryByCode, getCountryByLocale } from "./lib/countries";
import { ClientOnly } from "./lib/ui";
import { getHints } from "./lib/utils/client-hints";
import { createUrlTemplate, getDomainUrl } from "./lib/utils/url";
import { invariant } from "./lib/utils/utils";
import { DeferH1Seo, DeferSeo } from "./seo/seo";
import "./styles/app.css";
import "./styles/custom-fonts.css";
import "./styles/radix-themes/colors-light.css";
import "./styles/radix-themes/radius.css";
import "./styles/radix-themes/scaling.css";
import "./styles/radix-themes/storefront.css";
import "./styles/radix-themes/theme.css";
import "./styles/swiper.css";

export { RootErrorBoundary as ErrorBoundary } from "~/components/error-boundries/root-error-boundary";

export const loader = async ({ request, context }: LoaderArgs) => {
  const { locale, currency, site, lang, cspNonce } = context;
  const translations = await fetchTranslationsFromKv(lang);
  const preferredCountryCode = context.session.get("preferredCountryCode");
  const { env } = getSessionServerContext();
  const IMAGES_HOST = env?.IMAGES_HOST;
  const TOP_BAR_MESSAGES_INTERVAL = env?.TOP_BAR_MESSAGES_INTERVAL;
  const DEFAULT_SEARCH_TERM = env?.DEFAULT_SEARCH_TERM;
  let preferredCountry =
    preferredCountryCode && getCountryByCode(preferredCountryCode);
  if ((preferredCountry && preferredCountry.locale) !== locale.id) {
    preferredCountry = getCountryByLocale(locale.id);
  }

  const requestCountryCode = getContext().request.cf?.isEUCountry
    ? DEFAULT_COUNTRY_CODES.GB
    : DEFAULT_COUNTRY_CODES.US;
  //TODO: Redirect if request is not on the preferred site
  // const [preferredLocale, Site] = (preferredCountryCode &&
  //   getLocaleByCountry(preferredCountryCode)) || [undefined, undefined];
  return {
    locale,
    cspNonce,
    currency,
    site,
    lang,
    translations,
    APP_DEFAULT_LANG,
    IMAGES_HOST,
    TOP_BAR_MESSAGES_INTERVAL,
    DEFAULT_SEARCH_TERM,
    requestInfo: {
      ismobile: context.ismobile,
      requestCountryCode,
      isEUCountry: getContext().request.cf?.isEUCountry,
      hints: getHints(request),
      origin: getDomainUrl(request),
      /** *preferred country from session* or request country if not set */
      preferredCountry:
        preferredCountry || getCountryByCode(requestCountryCode),
      /** actual request country */
      country: requestCountryCode
        ? getCountryByCode(requestCountryCode)
        : undefined,
    },
  };
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  formData,
  currentParams: { locale: currentLocale },
  nextParams: { locale: nextLocale },
}) => {
  if (["setCountry"].includes(formData?.get("action")?.toString() || "")) {
    return true;
  }
  return currentLocale !== nextLocale;
};

export const links: LinksFunction = () => {
  return [];
};

export const useRootLayoutData = () => {
  // const matches = useMatches();
  const data = useRouteLoaderData<typeof loader>("root");
  invariant(data, "useRootLayoutData must be used within root layout");
  return data;
};

export function AppRoot({ children }: { children: React.ReactNode }) {
  const { lang, site, locale, translations, APP_DEFAULT_LANG } =
    useLoaderData<typeof loader>();
  const buildUrl = createUrlTemplate(locale);
  return (
    <>
      <SiteProvider buildUrl={buildUrl} locale={locale} site={site}>
        <IntlProvider
          onError={err => {
            if (err.code === "MISSING_TRANSLATION") {
              /* NOTE: Remove the console error for missing translations during development, */
              /* as we knew translations would be added later. */
              // console.warn("Missing translation", err.message);
              return;
            }
            console.error(err);
            // throw err;
          }}
          locale={lang}
          messages={translations}
          // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
          // NOTE: if you update this value, please also update the following npm scripts in `package.json`:
          // - "extract-default-translations"
          // - "compile-translations:pseudo"
          defaultLocale={APP_DEFAULT_LANG}
        >
          {children}
          <Toaster />
          {/* Google Analytics 4 */}
          <ClientOnly>{() => <GA4 />}</ClientOnly>
        </IntlProvider>
      </SiteProvider>
    </>
  );
}

export const useRootLoaderData = () => {
  const data = useRouteLoaderData<typeof loader>("root");
  invariant(data, "Root loader data is missing");
  return data;
};

export default function App() {
  // Get the locale from the loader
  const { IMAGES_HOST, cspNonce } = useRootLoaderData();
  const [searchParams] = useSearchParams();
  const preview = !!searchParams.get("preview");

  return (
    <ContentfulLivePreviewProvider
      // currently our contentful settings support only "en-US" translation, so in order for the preview functionality to work, we need to pass it as a value for the locale below
      locale={"en-US"}
      enableInspectorMode={preview}
      enableLiveUpdates={preview}
    >
      {IMAGES_HOST && (
        <InjectImagesHostScript IMAGES_HOST={IMAGES_HOST} nonce={cspNonce} />
      )}
      <div
        id="backdrop-quick-search"
        className="absolute top-0 z-50 hidden h-[100%] w-[100%] bg-black bg-opacity-40"
      ></div>
      <DeferH1Seo />
      <OnLocationChange />
      <AppRoot>
        <Outlet />
      </AppRoot>
    </ContentfulLivePreviewProvider>
  );
}

/**
 * Sets the global IMAGES_HOST variable on the client
 * @summary This script is used to set the global IMAGES_HOST variable on the client intentionally kept at root level
 */
const InjectImagesHostScript = memo(function InjectImagesHostScript({
  IMAGES_HOST,
  nonce,
}: {
  IMAGES_HOST: string;
  nonce: string;
}) {
  useMemo(() => {
    // Only run on the client
    if (typeof window !== "undefined") {
      globalThis.IMAGES_HOST = IMAGES_HOST;
    }
  }, [IMAGES_HOST]);

  return (
    <script
      // Browsers security remove nonce attributes when rendrered on the server so we can't hydrate that
      suppressHydrationWarning
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: `window.IMAGES_HOST = ${JSON.stringify(IMAGES_HOST)};`,
      }}
    />
  );
});

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const data = useRouteLoaderData<typeof loader>("root");
  const { lang } = data || {};
  const error = useRouteError();
  const isError = !!error;
  return (
    <html lang={lang} className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="preconnect" href="https://images.ctfassets.net/" />
        {/* only load this if this is not error */}
        {!isError && <DeferSeo />}
        <Meta />
        {/* <ClientHintCheck nonce={""} /> */}
        {/* All meta exports on all routes will go here */}
        {/* All link exports on all routes will go here */}
        <Links />
      </head>
      <body className="rendered bg-brand-background relative min-h-full text-base tracking-normal [&_iframe]:pointer-events-auto">
        {children}

        {/* Manages scroll position for client-side transitions */}
        {/* If you use a nonce-based content security policy for scripts, you must provide the `nonce` prop. Otherwise, omit the nonce prop as shown here. */}
        <ScrollRestoration nonce={data?.cspNonce} />
        {/* Script tags go here */}
        {/* If you use a nonce-based content security policy for scripts, you must provide the `nonce` prop. Otherwise, omit the nonce prop as shown here. */}
        <Scripts nonce={data?.cspNonce} />
        {/* Sets up automatic reload when you change code */}
        {/* and only does anything during development */}
        {/* If you use a nonce-based content security policy for scripts, you must provide the `nonce` prop. Otherwise, omit the nonce prop as shown here. */}
      </body>
    </html>
  );
};

/**
 * This component is used to remove the no-transition class from the body when the location changes.
 * enabled css transitions
 */
const OnLocationChange = () => {
  const location = useLocation();
  const ref = useRef<ReturnType<typeof useLocation> | null>(location);
  useEffect(() => {
    // document.body.classList.add(noTransitionStyles["enable-transitions"]);
    ref.current = null;
  }, []);
  return null;
};
